import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatExpansionModule } from '@angular/material/expansion';
import { AnalysisJobDetail } from '@app/types';
import { ClipboardModule } from '@angular/cdk/clipboard';

@Component({
  selector: 'app-analysis-detail-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatTabsModule, MatIconModule, ClipboardModule, MatExpansionModule],
  template: `
    <h2 mat-dialog-title>{{data.document_folio}}</h2>
    <mat-dialog-content>
      <div class="summary-grid">
        <div class="summary-item">
          <span class="label">Estado:</span>
          <span class="value" [class]="'status-' + data.status.toLowerCase()">{{data.status}}</span>
        </div>
        <div class="summary-item">
          <span class="label">Duración:</span>
          <span class="value">{{data.duration_seconds}}s</span>
        </div>
        <div class="summary-item">
          <span class="label">Intentos:</span>
          <span class="value">{{data.attempts.length || 0}}</span>
        </div>
        <div class="summary-item" *ngIf="data.total_pages_processed">
          <span class="label">Páginas:</span>
          <span class="value">{{data.total_pages_processed}}</span>
        </div>
      </div>
      
      <div class="summary-text" *ngIf="data.final_summary">
        <strong>Resumen:</strong> {{data.final_summary}}
      </div>
      
      <mat-accordion class="attempts-accordion" *ngIf="data.attempts && data.attempts.length > 0">
        <mat-expansion-panel *ngFor="let attempt of data.attempts; let i = index" [expanded]="i === data.attempts.length - 1">
          <mat-expansion-panel-header>
            <mat-panel-title>
              <span class="attempt-number">#{{attempt.number}}</span>
              <span class="attempt-type">{{attempt.type}}</span>
            </mat-panel-title>
            <mat-panel-description>
              <span [class]="'attempt-status status-' + attempt.status.toLowerCase()">{{attempt.status}}</span>
              <span class="attempt-page" *ngIf="attempt.page_num">P{{attempt.page_num}}</span>
              <span class="attempt-attrs" *ngIf="attempt.target_attributes && attempt.target_attributes.length > 0">
                {{attempt.target_attributes.length}} attrs
              </span>
            </mat-panel-description>
          </mat-expansion-panel-header>
          
          <div class="attempt-body">
            
            <div *ngIf="attempt.type === 'XML_PARSER'" class="xml-info-block">
                <mat-icon class="xml-icon">code</mat-icon>
                <span>Este documento fue procesado nativamente como XML. No se utilizó IA.</span>
            </div>
            <div class="attempt-meta" *ngIf="attempt.decision_log">
              <mat-icon class="meta-icon">info</mat-icon>
              <span>{{attempt.decision_log}}</span>
            </div>
            
            <div class="attempt-meta" *ngIf="attempt.target_attributes && attempt.target_attributes.length > 0">
              <mat-icon class="meta-icon">label</mat-icon>
              <span>{{attempt.target_attributes.join(', ')}}</span>
            </div>
            
            <div class="code-block" *ngIf="attempt.prompt_used">
              <div class="code-header">
                <span>Prompt</span>
                <button mat-icon-button [cdkCopyToClipboard]="attempt.prompt_used" class="copy-btn" title="Copiar">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
              <pre>{{attempt.prompt_used}}</pre>
            </div>
            
            <div class="code-block" *ngIf="attempt.raw_response">
              <div class="code-header">
                <span>Respuesta IA</span>
                <button mat-icon-button [cdkCopyToClipboard]="attempt.raw_response" class="copy-btn" title="Copiar">
                  <mat-icon>content_copy</mat-icon>
                </button>
              </div>
              <pre>{{attempt.raw_response}}</pre>
            </div>
          </div>
        </mat-expansion-panel>
      </mat-accordion>
      
      <div class="no-data" *ngIf="!data.attempts || data.attempts.length === 0">
        No hay intentos registrados
      </div>
    </mat-dialog-content>
    <mat-dialog-actions align="end">
      <button mat-button mat-dialog-close>Cerrar</button>
    </mat-dialog-actions>
  `,
  styles: [`
    h2 { 
      margin: 0; 
      font-size: 18px; 
      font-weight: 600;
    }
    
    mat-dialog-content {
      padding: 16px 24px !important;
      max-height: 70vh;
      overflow-y: auto;
    }
    
    .summary-grid {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
      gap: 12px;
      margin-bottom: 12px;
      padding: 12px;
      background: #f5f5f5;
      border-radius: 4px;
    }
    
    .summary-item {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .label {
      font-size: 11px;
      color: #666;
      text-transform: uppercase;
      font-weight: 500;
    }
    
    .value {
      font-size: 14px;
      font-weight: 600;
    }
    
    .status-completado, .status-success { color: #2e7d32; }
    .status-parcial { color: #ed6c02; }
    .status-error, .status-failed { color: #d32f2f; }
    .status-retry_needed { color: #0288d1; }
    .status-en_proceso { color: #9c27b0; }
    
    .summary-text {
      font-size: 12px;
      padding: 8px 12px;
      background: #e3f2fd;
      border-radius: 4px;
      margin-bottom: 12px;
    }
    
    .attempts-accordion {
      margin-top: 12px;
    }

    .xml-info-block {
      background-color: #e8f5e9;
      color: #2e7d32;
      padding: 12px;
      border-radius: 4px;
      margin-bottom: 12px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
      font-size: 13px;
    }
    .xml-icon { font-size: 20px; width: 20px; height: 20px; }
    
    mat-expansion-panel {
      margin-bottom: 8px !important;
      box-shadow: 0 1px 3px rgba(0,0,0,0.1) !important;
    }
    
    mat-panel-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 13px;
    }
    
    .attempt-number {
      background: #1976d2;
      color: white;
      padding: 2px 8px;
      border-radius: 12px;
      font-size: 11px;
      font-weight: 600;
    }
    
    .attempt-type {
      font-size: 12px;
      color: #666;
    }
    
    mat-panel-description {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 11px;
    }
    
    .attempt-status {
      font-weight: 600;
      font-size: 11px;
    }
    
    .attempt-page {
      background: #9c27b0;
      color: white;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
      font-weight: 600;
    }
    
    .attempt-attrs {
      background: #e0e0e0;
      padding: 2px 6px;
      border-radius: 10px;
      font-size: 10px;
    }
    
    .attempt-body {
      padding: 12px 0;
    }
    
    .attempt-meta {
      display: flex;
      align-items: flex-start;
      gap: 8px;
      padding: 8px 12px;
      background: #f9f9f9;
      border-radius: 4px;
      margin-bottom: 8px;
      font-size: 11px;
      line-height: 1.4;
    }
    
    .meta-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      color: #666;
    }
    
    .code-block {
      position: relative;
      background: #263238;
      border-radius: 4px;
      margin-bottom: 8px;
      overflow: hidden;
    }
    
    .code-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 12px;
      background: #37474f;
      color: #fff;
      font-size: 11px;
      font-weight: 600;
      text-transform: uppercase;
    }
    
    .copy-btn {
      width: 28px;
      height: 28px;
      line-height: 28px;
    }
    
    .copy-btn mat-icon {
      font-size: 16px;
      width: 16px;
      height: 16px;
      color: #fff;
    }
    
    pre {
      margin: 0;
      padding: 12px;
      white-space: pre-wrap;
      word-wrap: break-word;
      font-family: 'Courier New', monospace;
      font-size: 10px;
      line-height: 1.4;
      color: #aed581;
      max-height: 200px;
      overflow-y: auto;
    }
    
    .no-data {
      padding: 24px;
      text-align: center;
      color: #999;
      font-size: 12px;
    }
    
    mat-dialog-actions {
      padding: 12px 24px !important;
    }
  `]
})
export class AnalysisDetailDialogComponent {
  constructor(@Inject(MAT_DIALOG_DATA) public data: AnalysisJobDetail) { }
}
